/**
 * @date: 2022-04-13
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {ApiUrl} from "../utils/portal-urls";
import {handleHttpResponse} from "../utils/http-helpers";
import {terminologyByBatchType} from "../utils/terminology";


const initialState = {
    nppesBatches: [],
    latestNppesBatch: {},
    nuccBatches: [],
    latestNuccBatch: {},
    servDescriptionBatches: [],
    latestServDescriptionBatch: {},
    suffixBatches: [],
    latestSuffixBatch: {},
    orgTypeBatches: [],
    latestOrgTypeBatch: {},
    // Amendment #1 A5 — keyed by TerminologyImport name rather than a field per value set, so a new
    // value set needs no new state. Each entry is an array of batches, same shape as the fields above.
    terminologyBatches: {},
    latestTerminologyBatch: {},
    batchLoading: false,
    batchMsg: {open: false, msg: "", msgLv: ""},
    batchRefresh: new Date().getTime(),

}


export const getAllBatches = createAsyncThunk(
    "allBatches",
    async () => {
        const res = await fetch(ApiUrl.Batch.getAllActiveBatch.getPath);
        return await handleHttpResponse(res);
    }
)

export const allBatchesSlice = createSlice({
    name: "allBatches",
    initialState,
    reducers: {
        closeBatchAlert: (state, action) => {
            state.batchMsg = {open: false, msg: "", msgLv: ""};
        },
        update: (state, action) => {
            const { type, id, batch} = action.payload;
            switch (type) {
                case 'nppes':
                    if (batch.status === 'Completed') {
                        state.batchRefresh = new Date().getTime();
                    }
                    state.nppesBatches.map( (c, index) => {
                        if (c.id === id) {
                            state.nppesBatches[index] = batch;
                        }
                    })
                    break;
                case 'nucc':
                    if (batch.status === 'Completed') {
                        state.batchRefresh = new Date().getTime();
                    }
                    state.nuccBatches.map( (c, index) => {
                        if (c.id === id) {
                            state.nuccBatches[index] = batch;
                        }
                    })
                    break;
                case 'servDescription':
                    if (batch.status === 'Completed') {
                        state.batchRefresh = new Date().getTime();
                    }
                    state.servDescriptionBatches.map( (c, index) => {
                        if (c.id === id) {
                            state.servDescriptionBatches[index] = batch;
                        }
                    })
                    break;
                case 'suffix':
                    if (batch.status === 'Completed') {
                        state.batchRefresh = new Date().getTime();
                    }
                    state.suffixBatches.forEach( (c, index) => {
                        if (c.id === id) {
                            state.suffixBatches[index] = batch;
                        }
                    })
                    break;
                case 'orgType':
                    if (batch.status === 'Completed') {
                        state.batchRefresh = new Date().getTime();
                    }
                    state.orgTypeBatches.forEach( (c, index) => {
                        if (c.id === id) {
                            state.orgTypeBatches[index] = batch;
                        }
                    })
                    break;
                default: {
                    // A TerminologyImport name; anything else falls through harmlessly.
                    const list = state.terminologyBatches[type];
                    if (list) {
                        if (batch.status === 'Completed') {
                            state.batchRefresh = new Date().getTime();
                        }
                        list.forEach( (c, index) => {
                            if (c.id === id) {
                                list[index] = batch;
                            }
                        })
                    }
                    break;
                }
            }
        },
        addLatestBatch: (state, action) => {
            const { type, id, batch } = action.payload;
            switch (type) {
                case 'nppes':
                    state.latestNppesBatch[id] = batch;
                    break;
                case 'nucc':
                    state.latestNuccBatch[id] = batch;
                    break;
                case 'servDescription':
                    state.latestServDescriptionBatch[id] = batch;
                    break;
                case 'suffix':
                    state.latestSuffixBatch[id] = batch;
                    break;
                case 'orgType':
                    state.latestOrgTypeBatch[id] = batch;
                    break;
                default:
                    state.latestTerminologyBatch[id] = batch;
                    break;
            }
        },
        batchOnRefresh: (state, action) => {
            state.batchRefresh = new Date().getTime();
        },
        cancelBatch: (state, action) => {
            const { type, id } =  action.payload;
            switch (type) {
                case 'nppes':
                    state.nppesBatches = state.nppesBatches.filter( c => c.id !== id);
                    break;
                case 'nucc':
                    state.nuccBatches = state.nuccBatches.filter( c => c.id !== id);
                    break;
                case 'servDescription':
                    state.servDescriptionBatches = state.servDescriptionBatches.filter( c => c.id !== id);
                    break;
                case 'suffix':
                    state.suffixBatches = state.suffixBatches.filter( c => c.id !== id);
                    break;
                case 'orgType':
                    state.orgTypeBatches = state.orgTypeBatches.filter( c => c.id !== id);
                    break;
                default:
                    if (state.terminologyBatches[type]) {
                        state.terminologyBatches[type] = state.terminologyBatches[type].filter( c => c.id !== id);
                    }
                    break;
            }

            state.batchRefresh = new Date().getTime();
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllBatches.pending, (state, action) => {
                state.batchLoading = true;
            })
            .addCase(getAllBatches.rejected, (state, action) => {
                state.batchLoading = false;
                if (action.payload) {
                    const message = action.payload
                    state.batchMsg = {open: true, msg: message, msgLv: 'error'}
                }

            })
            .addCase(getAllBatches.fulfilled, (state, action) => {
                const data = action.payload;
                state.nppesBatches = [];
                state.nuccBatches = [];
                state.servDescriptionBatches = [];
                state.suffixBatches = [];
                state.orgTypeBatches = [];
                state.terminologyBatches = {};

                if (data) {
                    data.forEach( s => {
                        switch (s.type) {
                            case 'ImportNPIIncremental':
                            case 'ImportNPIFullDump':
                            case 'CheckNPI':
                            case 'DeactivateNPIs':
                                state.nppesBatches.push(s);
                                break;
                            case 'ImportSpecialtyValueSets':
                            case 'CleanAndImportTaxonomyCodes':
                                state.nuccBatches.push(s);
                                break;
                            case 'ImportValueSet':
                                state.servDescriptionBatches.push(s);
                                break;
                            case 'ImportProvSuffixVS':
                                state.suffixBatches.push(s);
                                break;
                            case 'ImportOrgTypeVS':
                                state.orgTypeBatches.push(s);
                                break;
                            default: {
                                // Amendment #1 A5 batch types, bucketed by value set.
                                const terminology = terminologyByBatchType(s.type);
                                if (terminology) {
                                    (state.terminologyBatches[terminology.key] ??= []).push(s);
                                }
                                break;
                            }
                        }
                    })
                }
            })
    }
})

export const { update, addLatestBatch, batchOnRefresh, cancelBatch } = allBatchesSlice.actions;

export default allBatchesSlice.reducer;
